import React, { useReducer, useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { advanceTimeline } from "../../lib/lessonVideo";
import InstructorSprite from "./InstructorSprite";

// <LessonVideoPlayer script={{voice,beats}} onComplete/>
//
// State is {index, playing} and is mutated ONLY through advanceTimeline(
// state, event, beats.length). Events: play | pause | next | prev | ended.
// Per beat: if beat.audioUrl, an <audio autoPlay onEnded> drives the "ended"
// event; otherwise a DEV fallback speaks the narration via speechSynthesis
// (SSR-safe: guarded + feature-detected, no window access at module top).
// onComplete() fires exactly once when the final beat's "ended" resolves.

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Wrap = styled.section`
  font-family: "Space Grotesk", sans-serif;
  color: var(--nd-text-primary);
  background: var(--nd-surface-elevated, rgba(255, 255, 255, 0.04));
  border: 1px solid var(--nd-border-visible, rgba(255, 255, 255, 0.1));
  border-radius: 12px;
  padding: 24px;
  max-width: 720px;
`;

const Stage = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const SpriteCol = styled.div`
  display: flex;
  justify-content: center;
`;

const RightCol = styled.div`
  min-width: 0;
  width: 100%;
`;

const Caption = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: var(--nd-text-display);
  margin: 0 0 12px;
  min-height: 48px;
`;

const CodePanel = styled.pre`
  font-family: "Space Mono", monospace;
  font-size: 12px;
  line-height: 1.6;
  background: var(--nd-surface-elevated, rgba(0, 0, 0, 0.35));
  border: 1px solid var(--nd-border-visible, rgba(255, 255, 255, 0.1));
  border-radius: 8px;
  color: var(--nd-text-primary);
  padding: 14px 16px;
  margin: 0;
  overflow-x: auto;
  white-space: pre;
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid var(--nd-border-visible, rgba(255, 255, 255, 0.1));
  padding-top: 16px;
`;

const Btn = styled.button`
  font-family: "Space Mono", monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible, rgba(255, 255, 255, 0.15));
  background: transparent;
  color: var(--nd-text-primary);
  cursor: pointer;
  transition: opacity 180ms ease;
  &:hover:not(:disabled) { opacity: 0.7; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const PlayBtn = styled(Btn)`
  background: var(--nd-text-display);
  color: var(--nd-black, #000);
  border-color: transparent;
`;

const Counter = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--nd-text-disabled);
  margin-left: auto;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const LessonVideoPlayer = ({ script, onComplete }) => {
  const beats = (script && Array.isArray(script.beats)) ? script.beats : [];
  const beatCount = beats.length;

  const [state, dispatch] = useReducer(
    (s, event) => advanceTimeline(s, event, beatCount),
    { index: 0, playing: false }
  );
  const { index, playing } = state;
  const [captionsOn, setCaptionsOn] = useState(true);

  const beat = beats[index] || null;
  const completedRef = useRef(false);
  const audioRef = useRef(null);

  // Drive the audio element imperatively. The `autoplay` attribute only fires
  // at mount, so flipping it on the Play click after the element already
  // mounted never starts playback — call play()/pause() directly instead. The
  // <audio> re-mounts per beat (keyed by index), so this fires for each beat.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !beat || !beat.audioUrl) return undefined;
    if (playing) {
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      el.pause();
    }
    return undefined;
  }, [playing, index]);

  // Speak the current beat's narration via the browser fallback while playing
  // (only when there's no audioUrl). SSR-safe: everything touching window is
  // inside the effect, guarded and feature-detected.
  useEffect(() => {
    if (!playing || !beat || beat.audioUrl) return;
    if (typeof window === "undefined") return;
    const synth = window.speechSynthesis;
    if (!synth || typeof window.SpeechSynthesisUtterance !== "function") {
      // No TTS available (SSR / unsupported): no-op, leave beat paused-in-place.
      return;
    }
    const utterance = new window.SpeechSynthesisUtterance(beat.narration);
    utterance.onend = () => dispatch("ended");
    synth.cancel();
    synth.speak(utterance);
    return () => {
      utterance.onend = null;
      synth.cancel();
    };
  }, [playing, index]);

  // onComplete: the final beat's "ended" resolves to {index: last, playing:false}
  // (advanceTimeline stops there). Detect that exact transition — previously
  // playing on the last beat, now stopped on the last beat — and fire once.
  const prevRef = useRef({ index: 0, playing: false });
  useEffect(() => {
    const prev = prevRef.current;
    const wasLastAndPlaying =
      prev.index === beatCount - 1 && prev.playing === true;
    const nowStoppedAtLast =
      index === beatCount - 1 && playing === false;
    if (
      beatCount > 0 &&
      wasLastAndPlaying &&
      nowStoppedAtLast &&
      !completedRef.current
    ) {
      completedRef.current = true;
      if (typeof onComplete === "function") onComplete();
    }
    prevRef.current = { index, playing };
  }, [index, playing, beatCount, onComplete]);

  const onAudioEnded = useCallback(() => dispatch("ended"), []);

  if (!beat) {
    return (
      <Wrap>
        <Caption>No script loaded.</Caption>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Stage>
        <SpriteCol>
          <InstructorSprite action={beat.action} scale={5} />
        </SpriteCol>
        <RightCol>
          {captionsOn && <Caption>{beat.caption}</Caption>}
          {beat.code && <CodePanel>{beat.code}</CodePanel>}
        </RightCol>
      </Stage>

      {/* Audio track for this beat, when provided. Re-mounts per beat via key
          so the ref rebinds and onEnded fires fresh each time; playback is
          driven imperatively by the effect above (not the autoplay attr). */}
      {beat.audioUrl && (
        <audio
          key={`beat-audio-${index}`}
          ref={audioRef}
          src={beat.audioUrl}
          onEnded={onAudioEnded}
        />
      )}

      <Controls>
        <PlayBtn onClick={() => dispatch(playing ? "pause" : "play")}>
          {playing ? "pause" : "play"}
        </PlayBtn>
        <Btn onClick={() => dispatch("prev")} disabled={index === 0}>
          prev
        </Btn>
        <Btn
          onClick={() => dispatch("next")}
          disabled={index === beatCount - 1}
        >
          next
        </Btn>
        <Btn onClick={() => setCaptionsOn((v) => !v)}>
          {captionsOn ? "captions off" : "captions on"}
        </Btn>
        <Counter>
          {index + 1} / {beatCount}
        </Counter>
      </Controls>
    </Wrap>
  );
};

export default LessonVideoPlayer;
